import React, { useCallback } from "react"
import styled from "styled-components"
import { State } from "@hookstate/core"
import { Validation } from "@hookstate/validation"
import { replacePlaceholder, useLanguageState } from "../../globalStates/LanguageState"
import { useExhibitorDataState } from "../../globalStates/ExhibitorDataState"
import Section from "../../components/Section"
import Dropzone from "../../components/Dropzone"
import AdItem from "./AdItem"
import { addElement, removeElementById } from "../../globalStates/utils"
import { newId } from "../../utils/Id"
import { useConfigDataState } from "../../globalStates/ConfigDataState"
import { PrintAdPage } from "../../globalStates/ExhibitorDataTypes"

const AdHeaders = styled.div`
    display: flex;
    border-bottom: 1px solid #d9d9d9;
    padding: 10px 0;
    margin: 25px -27.5px;
    & > * {
        margin: 0 27.5px;
    }
`

const AdHeader = styled.div<{ width: number }>`
    flex: 0 0 ${(props) => (props.width ? `${props.width}px` : "1")};
`

const Description = styled.div`
    font-weight: normal;
    font-size: 16px;
    line-height: 24px;
    margin-top: 10px;
    margin-bottom: 30px;
    white-space: break-spaces;
`

interface AdsSectionProps {
    adStates: State<PrintAdPage[], Validation>
    readOnly: boolean
}

const AdsSection: React.FC<AdsSectionProps> = ({ adStates, readOnly }) => {
    const branding = useLanguageState().getStrings()
    const exiState = useExhibitorDataState()
    const serviceKeys = useConfigDataState().getServiceKeys()
    const adsLimit = serviceKeys.printAd || 0
    const remainingUploads = adsLimit <= adStates.length ? 0 : adsLimit - adStates.length

    const onDropHandler = useCallback(
        (
            file: File,
            onUploadProgress: (progress: number) => void,
            onSuccessCallback: () => void,
            onErrorCallback: (reason: string) => void
        ) => {
            exiState
                .upload("PRINT_AD_PAGE", file, onUploadProgress)
                .then((response) => {
                    onSuccessCallback()
                    response &&
                        addElement(adStates, {
                            id: newId().toString(),
                            adFileId: response.fileId,
                            adUrl: response.fileUrl,
                            name: response.fileName
                        })
                })
                .catch((reason) => onErrorCallback(reason))
        },
        [exiState, adStates]
    )

    const onRemoveMediaHandler = useCallback(
        (adId: string) => {
            removeElementById<PrintAdPage>(adStates, adId)
        },
        [adStates]
    )

    return (
        <Section title={branding.configuration.printPage.sctAds.lblAdsSection}>
            {branding.configuration.printPage.sctAds.lblAdsDecription && (
                <Description>{branding.configuration.printPage.sctAds.lblAdsDecription}</Description>
            )}
            <Dropzone
                accept="application/pdf"
                onDropFile={onDropHandler}
                placeholderTitle={replacePlaceholder(branding.configuration.printPage.sctAds.hntDropzoneTitle, {
                    count: String(remainingUploads)
                })}
                placeholderText={branding.configuration.printPage.sctAds.hntDropzoneProp}
                readonly={adStates.length >= adsLimit || readOnly}
            />
            <AdHeaders>
                <AdHeader width={60}>
                    <label>{branding.configuration.printPage.sctAds.lblHeaderPosition}</label>
                </AdHeader>
                <AdHeader width={133}>
                    <label>{branding.configuration.printPage.sctAds.lblHeaderPreview}</label>
                </AdHeader>
                <AdHeader width={0}>
                    <label>{branding.configuration.printPage.sctAds.lblHeaderFile}</label>
                </AdHeader>
                <AdHeader width={55}>&nbsp;</AdHeader>
            </AdHeaders>

            {adStates.map((adState) => (
                <AdItem key={adState.id.get()} adState={adState} onRemoveMedia={onRemoveMediaHandler} />
            ))}
        </Section>
    )
}

export default AdsSection
